import { motion } from 'framer-motion'
import {
  Award,
  Users,
  Target,
  Heart,
  Camera,
  Video,
  Mic,
  Edit,
  Star,
  Calendar,
  Trophy,
} from 'lucide-react'

const About = () => {
  const milestones = [
    {
      year: '1999',
      title: 'Company Founded',
      description: 'Started with a vision inspired by Bhavesh\'s wife to capture beautiful moments',
    },
    {
      year: '2002',
      title: 'First Wedding Portfolio',
      description: 'Established reputation in wedding videography and photography',
    },
    {
      year: '2005',
      title: 'Digital Transition',
      description: 'Embraced digital technology for enhanced video quality',
    },
    {
      year: '2010',
      title: 'Corporate Expansion',
      description: 'Expanded into corporate event management and documentation',
    },
    {
      year: '2015',
      title: 'HD Production',
      description: 'Upgraded to full HD video production capabilities',
    },
    {
      year: '2020',
      title: 'Live Streaming',
      description: 'Added live streaming services during pandemic',
    },
    {
      year: '2022',
      title: '4K Production',
      description: 'Upgraded to 4K video production capabilities',
    },
    {
      year: '2024',
      title: '25 Years Legacy',
      description: 'Celebrating 25 years of capturing precious moments',
    },
  ]

  const teamMembers = [
    {
      name: 'Bhavesh Trivedi',
      role: 'CEO & Founder',
      image: '/team/bhavesh.jpg',
      bio: 'With over 25 years of experience in video production, Bhavesh founded the company in 1999 based on his wife\'s inspiring vision to capture life\'s precious moments.',
      skills: ['Video Production', 'Creative Direction', 'Event Planning', 'Business Strategy'],
    },
    {
      name: 'Harsh Trivedi',
      role: 'CFO & Technical Expert',
      image: '/team/harsh.jpg',
      bio: 'Harsh combines financial expertise with technical knowledge to drive operational excellence and innovation.',
      skills: ['Financial Management', 'Technical Operations', 'System Architecture', 'Process Optimization'],
    },
  ]

  const values = [
    {
      icon: Heart,
      title: 'Passion',
      description: 'We pour our heart into every project, treating each event as if it were our own.',
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'We strive for perfection in every frame, every moment, and every detail.',
    },
    {
      icon: Users,
      title: 'Collaboration',
      description: 'We work closely with our clients to bring their unique vision to life.',
    },
    {
      icon: Target,
      title: 'Innovation',
      description: 'We embrace new technologies and creative approaches to stay ahead.',
    },
  ]

  const achievements = [
    { icon: Trophy, number: '25+', label: 'Years of Excellence' },
    { icon: Star, number: '4.9/5', label: 'Client Rating' },
    { icon: Users, number: '1000+', label: 'Happy Clients' },
    { icon: Calendar, number: '2500+', label: 'Events Completed' },
  ]

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pt-24"
    >
      {/* Hero Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              About <span className="gradient-text">Parth Video</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/70 max-w-4xl mx-auto leading-relaxed">
              We are passionate storytellers dedicated to capturing life's most precious moments through the lens of creativity and technical excellence.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ x: -50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                Our <span className="gradient-text">Story</span>
              </h2>
              <div className="space-y-4 text-white/80 leading-relaxed">
                <p>
                  Founded in 1999 by Bhavesh Trivedi, Parth Video began with a beautiful inspiration from his wife's vision to capture and preserve life's most precious moments through the art of videography and photography.
                </p>
                <p>
                  What started as a heartfelt mission has evolved over 25 years into a full-service video production and event management company, serving clients across the region with dedication, creativity, and unwavering commitment to quality.
                </p>
                <p>
                  Today, we're proud to have documented countless events over our quarter-century journey, from intimate weddings to large corporate gatherings, each one treated with the same level of care and attention to detail that has become our signature.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ x: 50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="glass-card p-8">
                <div className="grid grid-cols-2 gap-6">
                  {achievements.map((achievement, index) => {
                    const Icon = achievement.icon
                    return (
                      <motion.div
                        key={index}
                        initial={{ scale: 0 }}
                        whileInView={{ scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        className="text-center space-y-2"
                      >
                        <div className="p-3 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 w-fit mx-auto">
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="text-2xl font-bold gradient-text">
                          {achievement.number}
                        </div>
                        <div className="text-white/70 text-sm">
                          {achievement.label}
                        </div>
                      </motion.div>
                    )
                  })}
                </div>
              </div>
              
              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary-500/20 rounded-full blur-xl floating" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-secondary-500/20 rounded-full blur-xl floating" style={{ animationDelay: '1s' }} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-black/20">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              className="glass-card space-y-6"
            >
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white">Our Mission</h3>
              </div>
              <p className="text-white/80 leading-relaxed">
                To create exceptional visual experiences that capture the essence of every moment, 
                combining technical expertise with artistic vision to deliver memories that last a lifetime. 
                We believe every event has a unique story, and we're here to tell it beautifully.
              </p>
            </motion.div>

            <motion.div
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="glass-card space-y-6"
            >
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-full bg-gradient-to-r from-secondary-500 to-accent-500">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white">Our Vision</h3>
              </div>
              <p className="text-white/80 leading-relaxed">
                To be the leading video production and event management company in the region, 
                known for our creativity, reliability, and commitment to excellence. We envision a 
                future where every special moment is preserved with the artistry it deserves.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Our <span className="gradient-text">Values</span>
            </h2>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              These core values guide everything we do and shape how we serve our clients.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon
              return (
                <motion.div
                  key={index}
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-card text-center hover-lift group"
                >
                  <div className="p-4 rounded-2xl bg-gradient-to-r from-primary-500 to-secondary-500 w-fit mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">
                    {value.title}
                  </h3>
                  <p className="text-white/70 leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section-padding bg-black/20">
        <div className="container-custom">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Our <span className="gradient-text">Journey</span>
            </h2>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              A decade of growth, innovation, and memorable moments.
            </p>
          </motion.div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-primary-500 via-secondary-500 to-accent-500 rounded-full" />
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ x: index % 2 === 0 ? -50 : 50, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                >
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                    <div className="glass-card space-y-3">
                      <div className="text-2xl font-bold gradient-text">
                        {milestone.year}
                      </div>
                      <h3 className="text-xl font-semibold text-white">
                        {milestone.title}
                      </h3>
                      <p className="text-white/70">
                        {milestone.description}
                      </p>
                    </div>
                  </div>
                  
                  {/* Timeline dot */}
                  <div className="relative z-10">
                    <div className="w-6 h-6 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full border-4 border-white/20" />
                  </div>
                  
                  <div className="w-1/2" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Meet Our <span className="gradient-text">Team</span>
            </h2>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              The talented individuals who bring creativity and expertise to every project.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {teamMembers.map((member, index) => (
              <motion.div
                key={index}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card text-center hover-lift group"
              >
                <div className="relative mb-6">
                  <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 p-1">
                    <div className="w-full h-full rounded-full bg-white/10 flex items-center justify-center">
                      <User className="w-12 h-12 text-white" />
                    </div>
                  </div>
                  <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-green-500 rounded-full border-2 border-white" />
                </div>
                
                <h3 className="text-xl font-semibold text-white mb-2">
                  {member.name}
                </h3>
                <p className="text-primary-300 font-medium mb-4">
                  {member.role}
                </p>
                <p className="text-white/70 text-sm mb-4 leading-relaxed">
                  {member.bio}
                </p>
                
                <div className="space-y-2">
                  {member.skills.map((skill, skillIndex) => (
                    <span
                      key={skillIndex}
                      className="inline-block px-3 py-1 text-xs bg-white/10 rounded-full text-white/80 mr-2 mb-2"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Equipment & Technology */}
      <section className="section-padding bg-black/20">
        <div className="container-custom">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              State-of-the-Art <span className="gradient-text">Equipment</span>
            </h2>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              We invest in the latest technology to ensure the highest quality results for our clients.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Camera, title: '4K Cameras', description: 'Professional 4K video cameras for crystal-clear footage' },
              { icon: Mic, title: 'Audio Equipment', description: 'High-quality microphones and audio recording systems' },
              { icon: Video, title: 'Drone Technology', description: 'Aerial videography with professional drone equipment' },
              { icon: Edit, title: 'Editing Suite', description: 'Advanced post-production and color grading capabilities' },
            ].map((equipment, index) => {
              const Icon = equipment.icon
              return (
                <motion.div
                  key={index}
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-card text-center hover-lift group"
                >
                  <div className="p-4 rounded-2xl bg-gradient-to-r from-primary-500 to-secondary-500 w-fit mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">
                    {equipment.title}
                  </h3>
                  <p className="text-white/70 leading-relaxed">
                    {equipment.description}
                  </p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>
    </motion.div>
  )
}

// User icon component (since it's not in lucide-react by default)
const User = ({ className }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
  </svg>
)

export default About